/** @jsx jsx */
import React, { useState } from 'react';
import { grayScale } from 'components/styles';
import Accordion from 'components/molecules/Accordion';
import DataTable from 'components/organismos/DataTable/DataTable';
import Checkbox from 'components/molecules/Checkbox';
import FlexBox from 'components/atoms/FlexBox';
import PrimaryButton, { SecondaryButton } from 'components/atoms/Button';
import TextForm from 'components/molecules/TextForm';
import { css, jsx } from '@emotion/core';
import ToastModal from 'components/organismos/ToastModal';
import ConfirmModal from 'components/organismos/ConfirmModal';
import Modal from 'components/molecules/Modal';
import TabControl from 'components/atoms/TabControl';
import moment from 'moment';
import Table from 'components/molecules/Table';
import useToastNotification from 'hooks/useToastNotification';
import BlockUI from 'components/molecules/BlockUi';
import { IchangeLog } from '../interface';
import { postApproveStamp, postBulkApproveStamp } from '../api';
import { useApplyingTransferForm } from './hook';
import ApplyingStampDomain from '../domain/applyingStampDomain';
import { clearLine } from 'readline';
import { stateSymbol } from 'components/organismos/master/general/salesPage/SalesManagementList/hookSysboy';

const { errorNotification, successNotification } = useToastNotification();

const StampAccordion: React.FC<{
  list: ApplyingStampDomain[];
  setApplyingStampList: any;
  fetchApprovedList: any;
  fetchRejectedList: any;
}> = ({
  list, setApplyingStampList, fetchApprovedList, fetchRejectedList,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    toastMessage,
    setToastMessage,

    toastModalOpen,
    setToastModalOpen,

    confirmApplyModalOpen,
    setConfirmApplyModalOpen,

    closeConfirmApplyModal,

    extractSingleApproveData,
    extractBulkApproveData,
    extractSingleRejectData,

    rejectReasonOnchange,

    buttonType,
    setButtonType,
    selectedIndex,
    setSelectedIndex,

    changeLogData,
    setChangeLogData,
    changeLogModalOpen,
    setChangeLogModalOpen,
    selectChangelog,
    setSelectChangelog,
    fetchChangeLog,
  } = useApplyingTransferForm(list);

  const handleSubmit = async () => {
    const remainingList = list.filter(
      (item, position) => !selectedIndex.includes(position),
    ); // remove checked requests
    const checkedList = list.filter(
      (item, position) => selectedIndex.includes(position),
    ); // checked requests

    try {
      if (buttonType === 1) {
        rejectRequest(checkedList, remainingList);
      } else {
        approveRequest(checkedList, remainingList);
      }
    } catch (error) {
      setToastMessage(error.message);
    }
    setConfirmApplyModalOpen(false);
    setSelectedIndex([]);
  };

  const approveRequest = async (checkedList: any[], remainingList: any[]) => {
    setIsLoading(true);
    if (checkedList.length > 1) {
      await postBulkApproveStamp(extractBulkApproveData(checkedList)).then(() => {
        setIsLoading(false);
        successNotification('承認しました。');
        setApplyingStampList(remainingList); // set new list
        fetchApprovedList(); // refetch API
      }).catch((error) => {
        if (error.response && error.response.data && error.response.data.errors) {
          let msgError = '';
          error.response.data.errors.map((item: { defaultMessage: string; }) => {
            msgError += `${item.defaultMessage} </br>`;
            return msgError;
          });
          errorNotification(msgError);
        } else {
          errorNotification('サーバー側でエラーが発生しました。');
        }
        setIsLoading(false);
      });
    } else {
      await postApproveStamp(extractSingleApproveData(checkedList)).then(() => {
        setIsLoading(false);
        successNotification('承認しました。');
        setApplyingStampList(remainingList); // set new list
        fetchApprovedList(); // refetch API
      }).catch((error) => {
        setIsLoading(false);
        if (error.response && error.response.data && error.response.data.errors) {
          let msgError = '';
          error.response.data.errors.map((item: { defaultMessage: string; }) => {
            msgError += `${item.defaultMessage} </br>`;
            return msgError;
          });
          errorNotification(msgError);
        } else {
          errorNotification('サーバー側でエラーが発生しました。');
        }
      });
    }
  };

  const rejectRequest = async (checkedList: any[], remainingList: any[]) => {
    setIsLoading(true);
    if (!checkedList[0].rejectReason) {
      setIsLoading(false);
      errorNotification('否決コメントを入力してください。');
      return;
    }

    await postApproveStamp(extractSingleRejectData(checkedList)).then(() => {
      setIsLoading(false);
      successNotification('否決しました。');
      setApplyingStampList(remainingList); // set new list
      fetchRejectedList(); // refetch API
    }).catch((error) => {
      setIsLoading(false);
      if (error.response && error.response.data && error.response.data.errors) {
        let msgError = '';
        error.response.data.errors.map((item: { defaultMessage: string; }) => {
          msgError += `${item.defaultMessage} </br>`;
          return msgError;
        });
        errorNotification(msgError);
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
      }
    });
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (cell: {
          flatRows: any;
          state:any;
        }) => (
          <Checkbox
            id="checkbox_stamp_all"
            name="checkbox_stamp_all"
            value=""
            checked={(cell.state.pageSize === selectedIndex.length || cell.state.pageSize >= selectedIndex.length) && selectedIndex.length !== 0}
            onChange={() => {
              if (selectedIndex.length > 0) {
                setSelectedIndex([]);
              } else {
                const allIndex = cell.flatRows
                  .slice(cell.state.pageIndex * cell.state.pageSize, cell.state.pageSize * (cell.state.pageIndex + 1))
                  .map((i: { id: any }) => parseInt(i.id))
                setSelectedIndex(allIndex);
              }
            }}
          />
        ),
        disableSortBy: true,
        id: '',
        accessor: 'checkbox',
        Cell: (cell: {
          row: { isExpanded: boolean; original: any; index: number };
          data: any;
        }) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              id={`checkbox_stamp_${cell.row.index}`}
              name="name"
              label=""
              value={`${cell.row.index}`}
              checked={selectedIndex.includes(cell.row.index)}
              onChange={() => {
                if (selectedIndex.includes(cell.row.index)) {
                  setSelectedIndex(
                    selectedIndex.filter(
                      (selected) => selected !== cell.row.index,
                    ),
                  );
                } else {
                  setSelectedIndex([...selectedIndex, cell.row.index]);
                }
              }}
            />
          </div>
        ),
      },
      {
        Header: () => '決裁',
        id: 'decision',
        accessor: 'decision',
        Cell: (cell: {
          row: { isExpanded: boolean; original: any; index: number };
          data: any;
        }) => (
          <div>
            <FlexBox customStyle={css({ justifyContent: 'space-evenly' })}>
              <PrimaryButton
                text="承認"
                onClick={() => {
                  setButtonType(0);
                  setSelectedIndex([cell.row.index]);
                  setConfirmApplyModalOpen(true);
                }}
              />
              <SecondaryButton
                text="否決"
                onClick={() => {
                  setButtonType(1);
                  setSelectedIndex([cell.row.index]);
                  setConfirmApplyModalOpen(true);
                }}
              />
            </FlexBox>
            <TextForm
              name="rejectReason"
              label=""
              value={cell.data[cell.row.index].rejectReason}
              placeHolder="否決コメントを入力"
              errorMsg=""
              onChange={(e) => rejectReasonOnchange(cell.row.index, e)}
            />
          </div>
        ),
      },
      {
        Header: '組織名',
        accessor: 'orgName',
      },
      {
        Header: '申請者',
        accessor: 'applicant',
      },
      {
        Header: '申請日時',
        accessor: 'applicationDate',
      },
      {
        Header: '対象者',
        accessor: 'targetPerson',
      },
      {
        Header: () => '対象日付',
        id: '',
        accessor: 'targetDate',
        Cell: (cell: {
          row: { isExpanded: boolean; original: any; index: number };
          data: any;
        }) => (
          <div style={{ textAlign: 'center' }}>
            <div
              css={css`
                button {
                  background-color: #f0ad4e;
                  border-color: #e08e0b;
                  padding: 0.2rem;
                  line-height: 14px;
                }
              `}
            >
              <div>{cell.row.original.targetDate}</div>
              <PrimaryButton
                text="更新履歴"
                onClick={() => {
                  fetchChangeLog(cell.row.original.modifierApplicationId);
                  setChangeLogModalOpen(true);
                  setSelectChangelog('0');
                }}
              />
            </div>
          </div>
        ),
      },
      {
        Header: '申請種別',
        accessor: 'modifiyStatusName',
      },
      {
        Header: '出勤店舗',
        accessor: 'stampOrgName',
      },
      {
        Header: '開始時間', // starttime
        accessor: 'stampListStartTime', // .startTime
        Cell: (cell: { row :{ isExpanded: boolean, original: any }}) => (
          <div style={{ textAlign: 'left', whiteSpace: 'pre' }}>
            {cell.row.original.stampListStartTime}
          </div>
        ),
      },
      {
        Header: '終了時間', // endtime
        accessor: 'stampListEndTime', // .endTime
        Cell: (cell: { row :{ isExpanded: boolean, original: any }}) => (
          <div style={{ textAlign: 'left', whiteSpace: 'pre' }}>
            {cell.row.original.stampListEndTime && cell.row.original.stampListEndTime}
          </div>
        ),
      },
      {
        Header: '業務内容', // business content
        accessor: 'stampListBusinessName', // .businessName
        Cell: (cell: { row :{ isExpanded: boolean, original: any }}) => (
          <div style={{ textAlign: 'left', whiteSpace: 'pre' }}>
            {cell.row.original.stampListBusinessName}
          </div>
        ),
      },
      {
        Header: '申請理由',
        accessor: 'applicationReason',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, selectedIndex],
  );

  return (
    <div
      css={css`
      table th:nth-child(1) {
        width: 20px;
      }
      table th:nth-child(2) {
        width: 180px;
      }
      table th:nth-child(3) {
        width: 140px;
      }
      table th:nth-child(4) {
        width: 140px;
      }
      table th:nth-child(5) {
        width: 120px;
      }
      table th:nth-child(6) {
        width: 140px;
      }
      table th:nth-child(7) {
        width: 80px;
      }
      table th:nth-child(8) {
        width: 80px;
      }
      table th:nth-child(9) {
        width: 140px;
      }
      table th:nth-child(10) {
        width: 140px;
      }
      table th:nth-child(11) {
        width: 140px;
      }
      table th:nth-child(12) {
        width: 80px;
      }
      table th:nth-child(13) {
        width: 200px;
      }

      .modal-table th:nth-child(1) {
        width: 140px;
      }
      .modal-table th:nth-child(2) {
        width: 140px;
      }
      .modal-table th:nth-child(3) {
        width: 140px;
      }
      `}
    >
      <Accordion
        text={`打刻修正申請（${list.length}件）`}
        color={grayScale.gray100}
        callBack={() => {}}
      >
        <PrimaryButton
          text="一括承認"
          onClick={() => {
            setButtonType(2);
            setConfirmApplyModalOpen(true);
          }}
          disabled={selectedIndex.length === 0}
        />
        <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <BlockUI blocking={isLoading}>
            <DataTable columns={columns} data={list} isGlobalFilter={true} minWidth="1704px" sortBy={[{ id: 'applicationDate', desc: false }]} />
          </BlockUI>
        </div>
      </Accordion>

      <ToastModal
        open={toastModalOpen}
        closeHandler={() => setToastModalOpen(false)}
        title={toastMessage}
      />

      <ConfirmModal
        open={confirmApplyModalOpen}
        closeHandler={closeConfirmApplyModal}
        onSubmit={handleSubmit}
        title="確認メッセージ"
        // eslint-disable-next-line no-nested-ternary
        content={
          buttonType === 0
            ? '承認します。よろしいですか？'
            : buttonType === 1
              ? '否決します。よろしいですか？'
              : '一括承認します。よろしいですか？'
        }
        // eslint-disable-next-line no-nested-ternary
        submitText={buttonType !== 1 ? '承認' : '否決'}
      />

      {/* Change log  */}
      <div css={css('>div>div{ max-width: 600px }')}>
        <Modal
          open={changeLogModalOpen}
          closeHandler={() => {
            setChangeLogModalOpen(false);
            setChangeLogData([]);
          }}
          title="更新履歴"
          submitText="閉じる"
          onSubmit={() => {
            setChangeLogModalOpen(false);
            setChangeLogData([]);
          }}
          isShowButtonClose={false}
        >
          <TabControl
            items={changeLogData.map((log: IchangeLog, index: number) => ({
              value: String(index),
              display: `ver${log.version}`,
            }))}
            setValue={setSelectChangelog as (arg: string) => void}
            currentValue={selectChangelog}
            isFixed={true}
          />
          <div>
            {changeLogData.length > 0 && (
              <React.Fragment>
                <div>
                  {`申請種別: ${changeLogData[Number(selectChangelog)].historyTypeName}`}
                </div>
                <div>
                  {
                    changeLogData[Number(selectChangelog)].applicationStaffCode
                      ? `申請者: ${
                        changeLogData[Number(selectChangelog)].applicationStaffCode
                      } ${
                        changeLogData[Number(selectChangelog)].applicationStaffName
                      }`
                      : null
                }
                </div>
                <div>
                  {
                    changeLogData[Number(selectChangelog)].applicationDate
                      ? `申請日時: ${moment(
                        changeLogData[Number(selectChangelog)].applicationDate,
                      ).format('YYYY/MM/DD HH:mm:ss')}`
                      : null
                  }
                </div>
                <div>
                  {`出勤店舗: ${changeLogData[Number(selectChangelog)].orgCode} ${
                    changeLogData[Number(selectChangelog)].orgName
                  }`}
                </div>

                <div className="modal-table">
                  <Table>
                    <thead>
                      <tr>
                        <Table.HeaderCell customStyle={css({ width: '140px' })}>
                          開始時間
                        </Table.HeaderCell>
                        <Table.HeaderCell customStyle={css({ width: '140px' })}>
                          終了時間
                        </Table.HeaderCell>
                        <Table.HeaderCell customStyle={css({ width: '140px' })}>
                          業務内容
                        </Table.HeaderCell>
                      </tr>
                    </thead>
                    <tbody>
                      {changeLogData[Number(selectChangelog)].stampList.length
                      > 0
                      && changeLogData[Number(selectChangelog)].stampList.map(
                        (stamp, i) => (
                          <tr key={stamp.stampId}>
                            <Table.Cell customStyle={css({ textAlign: 'center' })}>
                              {moment(stamp.startTime).format(
                                'YYYY/MM/DD HH:mm:ss',
                              )}
                            </Table.Cell>
                            <Table.Cell customStyle={css({ textAlign: 'center' })}>
                              {
                                stamp.endTime
                                  ? moment(stamp.endTime).format(
                                    'YYYY/MM/DD HH:mm:ss',
                                  )
                                  : null
                              }
                            </Table.Cell>
                            <Table.Cell>{stamp.businessName}</Table.Cell>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </div>
                <div>
                  {
                    changeLogData[Number(selectChangelog)].applicationReason
                      ? `修正理由: ${
                        changeLogData[Number(selectChangelog)].applicationReason
                      }`
                      : null
                  }
                </div>

                {changeLogData[Number(selectChangelog)].approvalDate && (
                  <div>
                    承認日時:
                    {' '}
                    {moment(
                      changeLogData[Number(selectChangelog)].approvalDate,
                    ).format('YYYY/MM/DD HH:mm:ss')}
                  </div>
                )}

                {changeLogData[Number(selectChangelog)].approvalStaffCode && (
                  <div>
                    {`承認者: ${
                      changeLogData[Number(selectChangelog)].approvalStaffCode
                    }`}
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </Modal>
      </div>
      {/* ./Change log  */}
    </div>
  );
};

export default StampAccordion;
